import { useState, useContext, useEffect, useMemo } from 'react';
import { UnitsContext } from '../../../../Contexts/UnitsContext';
import BidderCard from './BidderCard';
import PageHeading from '../../../../Components/PageHeading';
import InputConfig from '../../../../Components/InputConfig';
import DropDownMultiple from '../../../../Components/DropDownMultiple';
import { getFramerateNetworkDocConfig } from '../../../../api/prebidDoc';
import Loader from '../../../../Components/Loader';

//mui imports====
import useCommonStyles from '../../../../commonStyles';
import { Grid, Paper } from '@mui/material';
//===============

const PrebidBiddersConfig = ({ keyData, featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const [biddersToBeAdded, setBiddersToBeAdded] = useState([]); // array shows the available bidders in the dropdown which can be added
	const {
		prebidRawConfig,
		updatePrebidRawConfig,
		framerateNetworkConfig,
		updateFramerateNetworkConfig
	} = useContext(UnitsContext);

	const biddersAvailable = prebidRawConfig?.prebid?.bids || [];

	/**
	 * Creates a simplified bidder config from framerateNetworkConfig.
	 *
	 * It picks default values from siteLevel, global, and adUnitLevel params
	 * and builds an object with bidder name and its params.
	 *
	 * Returns null if framerateNetworkConfig is not available.
	 */
	const transformedPrebidConfig = useMemo(() => {
		if (!framerateNetworkConfig) {
			return null;
		}

		const transformedConfig = {};

		for (const [bidderKey, bidderConfig] of Object.entries(framerateNetworkConfig)) {
			const params = {};
			const siteLevelParams = bidderConfig.params?.siteLevel || {};
			const globalParams = bidderConfig.params?.global || {};
			const adUnitLevelParams = bidderConfig.params?.adUnitLevel || {};
			const isActive = bidderConfig.isActive || false;

			const allParams = { ...siteLevelParams, ...globalParams, ...adUnitLevelParams };

			for (const [paramKey, paramConfig] of Object.entries(allParams)) {
				const defaultValue = paramConfig?.defaultValue;
				params[paramKey] = defaultValue ?? null;
			}

			if (isActive) {
				transformedConfig[bidderKey] = {
					bidder: bidderKey,
					params
				};
			}
		}

		return transformedConfig;
	}, [framerateNetworkConfig]);

	/**
	 * The function takes the total available bidders and filter bidders which are already present in the biddersAvailable array
	 * and return an array of bidders which can be added in dropdown
	 * @param BiddersArray - The total available bidders in the bidders json.
	 * @param BidderAvailable - The array of bidders object available in couchbase.
	 */
	useEffect(() => {
		if (!transformedPrebidConfig || !biddersAvailable) return;

		const updatedBidsArray = [];
		const biddersArray = Object.keys(transformedPrebidConfig);
		const biddersAlreadyPresent = new Set(
			biddersAvailable.map((bidderObject) => bidderObject.bidder)
		);

		biddersArray.forEach((bidderName) => {
			if (!biddersAlreadyPresent.has(bidderName)) {
				updatedBidsArray.push(bidderName);
			}
		});

		setBiddersToBeAdded(updatedBidsArray);
	}, [transformedPrebidConfig, biddersAvailable]);

	useEffect(() => {
		if (framerateNetworkConfig) return;

		const fetchPrebidConfig = async () => {
			try {
				const response = await getFramerateNetworkDocConfig();
				const framerateNetworksDoc = response?.data?.data?.framerateNetworksDoc;
				updateFramerateNetworkConfig(framerateNetworksDoc);
			} catch (error) {
				console.error('Error fetching initial prebid config:', error);
			}
		};

		fetchPrebidConfig();
	}, []);

	/**
	 * The function takes the bidderName and updates the prebidConfig with the new bidder Object and also filter out bidderName from dropdown array
	 * @param bidderName - The name of the bidder which is to be added in the prebidConfig array of object.
	 */
	const addBidders = (bidderName) => {
		const updatedBidders = [
			...(prebidRawConfig?.prebid?.bids || []),
			transformedPrebidConfig[bidderName]
		];

		updatePrebidRawConfig((obj) => {
			const prebidObj = structuredClone(obj);
			prebidObj.prebid.bids = updatedBidders;
			return prebidObj;
		}); //adds the bidder based upon the name of bidder into prebidConfig

		let updatedArray = [...biddersToBeAdded];
		updatedArray = updatedArray.filter((data) => data !== bidderName);
		setBiddersToBeAdded(updatedArray); //deletes the bidder from dropdown array
	};

	/**
	 * The function takes the updatedBidderObject and an bidderIndex in the bids array  and updates the prebidConfig with the new bidder Object
	 * @param updatedBidderObject - The updated bidder object which is to be updated in the prebidConfig array of object.
	 */
	const updateBidders = (updatedBidderObject, bidderIndex) => {
		updatePrebidRawConfig((obj) => {
			const prebidObject = structuredClone(obj);
			prebidObject.prebid.bids[bidderIndex] = updatedBidderObject;
			return prebidObject;
		});
	};

	/**
	 * The function takes the bidderName and deletes it from prebidConfig array and adds the same bidder to dropdown array
	 * @param bidderName - The name of the bidder which is to be added in the prebidConfig array of object.
	 */
	const deleteBidder = (bidderName) => {
		let updatedBidders = [...biddersAvailable];
		updatedBidders = updatedBidders.filter((data) => data.bidder !== bidderName);

		updatePrebidRawConfig((obj) => {
			const prebidObj = structuredClone(obj);
			prebidObj.prebid.bids = updatedBidders;
			return prebidObj;
		}); //deletes bidder from prebidConfig

		setBiddersToBeAdded([...biddersToBeAdded, bidderName]); //adds the bidder to dropdown array
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			<PageHeading
				headingName="Prebid"
				headingPath={['Dashboard', 'Framerate', 'Prebid']}
				description="Manage bidder settings for the site"
				showInfoButton={true}
				infoMessage={featureDescription}
			/>
			<Grid container spacing={1} style={{ marginTop: '4rem', width: '90%', marginLeft: '4rem' }}>
				<Grid item xs={3}>
					<InputConfig
						inputType="string"
						label="Site Code"
						keyData="code"
						object={prebidRawConfig?.prebid || {}}
						updateObject={(cb) => {
							const updatedPrebidObj = cb();
							const clonedPrebidRawObj = structuredClone(prebidRawConfig);
							clonedPrebidRawObj.prebid = { ...clonedPrebidRawObj.prebid, ...updatedPrebidObj };
							updatePrebidRawConfig(clonedPrebidRawObj);
						}}
					/>
				</Grid>
				<Grid item xs={3}>
					<DropDownMultiple
						keyData={'bids'}
						label="Add Bidder"
						list={biddersToBeAdded}
						formControlVariant="outlined"
						selectBlockStyles={{ width: '30rem', marginTop: '1rem' }}
						disabled={!transformedPrebidConfig}
						object={prebidRawConfig?.prebid?.bids || []}
						updateObject={(cb) => {
							const updatedPrebidBidsObj = cb();
							const bidsArray = updatedPrebidBidsObj?.bids || [];
							const newBidderAdded = bidsArray[0];
							addBidders(newBidderAdded);
						}}
					/>
				</Grid>
			</Grid>

			{transformedPrebidConfig ? (
				<Grid container style={{ marginTop: '1rem', padding: '1rem 3rem' }}>
					{biddersAvailable.map((bidder, id) => (
						<BidderCard
							key={id}
							bidderData={bidder}
							bidderTypes={framerateNetworkConfig}
							updateBidder={updateBidders}
							deleteBidder={deleteBidder}
							bidderIndex={id}
						/>
					))}
				</Grid>
			) : (
				<div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
					<Loader />
				</div>
			)}
		</Paper>
	);
};
export default PrebidBiddersConfig;
